import { useState, useEffect } from 'react'
import useMenuBack from '../../hooks/useMenuBack'
import { ArrowLeft, Lock, Check } from 'lucide-react'
import { authAPI } from '../../services/api'
import { ResponsiveWrapper } from '../../components/layout'
import { useI18n } from '../../i18n'
import { useUserStore } from '../../stores'
import { clearFaceIdPref } from '../../utils/faceIdPref'
import { OrellaSpinner } from '../../components/OrellaLoader' // s123: the app's one spinner

/**
 * SessionPinScreen v238.20.0 — dedicated screen for the lock-on-return Session PIN.
 *
 * Split out of SecurityScreen so PIN management has its own surface. On mount it
 * refreshes the user from /auth/me so the Active/Off state reflects server truth
 * (persists across reloads). Set / change / turn-off all hit the existing
 * /auth/session-pin endpoints and update the persisted user store.
 */
export default function SessionPinScreen() {
  const goBack = useMenuBack()
  const { t } = useI18n()

  const storeUser = useUserStore((s) => s.user)
  const setUserStore = useUserStore((s) => s.setUser)
  const refreshUser = useUserStore((s) => s.refreshUser)
  const sessionPinEnabled = !!storeUser?.sessionPinEnabled

  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(false)
  const [pinNew, setPinNew] = useState('')
  const [pinConfirm, setPinConfirm] = useState('')
  const [pinErr, setPinErr] = useState('')
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  const pinValid = /^\d{4}$/.test(pinNew) && pinNew === pinConfirm

  // Pull fresh server state so the badge persists correctly across reloads.
  useEffect(() => {
    let active = true
    ;(async () => {
      try { await refreshUser() } catch { /* non-blocking */ }
      if (active) setLoading(false)
    })()
    return () => { active = false }
  }, [refreshUser])

  const resetForm = () => { setPinNew(''); setPinConfirm(''); setPinErr('') }

  const savePin = async () => {
    if (!pinValid) {
      setPinErr(pinNew.length !== 4
        ? t('security.pinMustBe4', 'PIN must be 4 digits.')
        : t('security.pinsNoMatch', "PINs don't match."))
      return
    }
    setSaving(true)
    setPinErr('')
    try {
      await authAPI.setSessionPin(pinNew)
      if (storeUser) setUserStore({ ...storeUser, sessionPinEnabled: true })
      try { await refreshUser() } catch { /* non-blocking */ }
      resetForm()
      setEditing(false)
      setSaved(true)
      setTimeout(() => setSaved(false), 3000)
    } catch (e) {
      setPinErr(e.error || t('security.pinSaveFailed', 'Could not save PIN.'))
    } finally {
      setSaving(false)
    }
  }

  const disablePin = async () => {
    if (!confirm(t('security.pinDisableConfirm', "Turn off your session PIN? You'll sign in with full credentials each time you return."))) return
    setSaving(true)
    try {
      await authAPI.updateSessionPinSettings({ sessionPinEnabled: false })
      if (storeUser) setUserStore({ ...storeUser, sessionPinEnabled: false })
      clearFaceIdPref(storeUser?.id) // s114 Face ID opt-in: Face ID unlock means nothing without the PIN
      try { await refreshUser() } catch { /* non-blocking */ }
      resetForm()
      setEditing(false)
    } catch (e) {
      alert(e.error || t('security.pinUpdateFailed', 'Could not update PIN settings.'))
    } finally {
      setSaving(false)
    }
  }

  const showForm = !sessionPinEnabled || editing

  return (
    <ResponsiveWrapper className="bg-white shadow-xl">
    <div className="screen-min-h bg-white flex flex-col">
      {/* Header */}
      <div className="sticky-page-header px-4 pt-4 pb-2 flex items-center gap-3">
        <button onClick={() => goBack()} className="p-2 -ml-2 text-gray-600">
          <ArrowLeft size={24} />
        </button>
        <h1 className="text-xl font-bold text-orella-700">{t('security.sessionPin', 'Session PIN')}</h1>
      </div>

      <div className="flex-1 px-4 pb-8 overflow-auto">
        {loading ? (
          <div className="py-10 text-center">
            <OrellaSpinner size={24} className="mx-auto" />
          </div>
        ) : (
          <div className="mt-2">
            {/* Status row */}
            <div className="flex items-center gap-3 mb-4">
              <div className="w-11 h-11 rounded-full bg-orella-50 flex items-center justify-center">
                <Lock className="w-5 h-5 text-orella-500" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-gray-800">{t('security.sessionPin', 'Session PIN')}</p>
                <p className="text-xs text-gray-500">{t('security.sessionPinDesc', 'Quick PIN unlock when you return to the app')}</p>
              </div>
              {sessionPinEnabled ? (
                <span className="text-xs font-medium text-green-700 bg-green-100 px-2.5 py-0.5 rounded-full">{t('security.active', 'Active')}</span>
              ) : (
                <span className="text-xs font-medium text-amber-700 bg-amber-100 px-2.5 py-0.5 rounded-full">{t('security.off', 'Off')}</span>
              )}
            </div>

            {/* Saved confirmation */}
            {saved && (
              <div className="mb-3 p-2.5 bg-green-50 border border-green-200 rounded-xl flex items-center gap-2">
                <Check className="w-4 h-4 text-green-600" />
                <span className="text-sm text-green-700 font-medium">{t('security.pinSavedMsg', 'Session PIN saved')}</span>
              </div>
            )}

            {/* Explanation */}
            <div className="bg-gray-50 rounded-2xl p-4 mb-5">
              <p className="text-sm text-gray-600 leading-relaxed">
                {t('security.sessionPinExplain', "When enabled, you'll unlock with a 4-digit PIN instead of signing in with full credentials when you return to the app.")}
              </p>
            </div>

            {showForm ? (
              <div className="space-y-3">
                <div>
                  <label className="block text-sm text-gray-500 mb-1.5">{t('security.newPin', 'New 4-digit PIN')}</label>
                  <input
                    type="password"
                    inputMode="numeric"
                    maxLength={4}
                    value={pinNew}
                    onChange={(e) => { setPinNew(e.target.value.replace(/\D/g, '').slice(0, 4)); setPinErr('') }}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-orella-500 tracking-[0.4em] text-lg text-center"
                    placeholder="••••"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-500 mb-1.5">{t('security.confirmPin2', 'Confirm PIN')}</label>
                  <input
                    type="password"
                    inputMode="numeric"
                    maxLength={4}
                    value={pinConfirm}
                    onChange={(e) => { setPinConfirm(e.target.value.replace(/\D/g, '').slice(0, 4)); setPinErr('') }}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-orella-500 tracking-[0.4em] text-lg text-center"
                    placeholder="••••"
                  />
                </div>
                {pinErr && <p className="text-red-500 text-sm">{pinErr}</p>}
                <div className="flex gap-2 pt-1">
                  {editing && (
                    <button
                      onClick={() => { setEditing(false); resetForm() }}
                      className="flex-1 py-3 bg-white border border-gray-200 rounded-full font-semibold text-sm text-gray-600"
                    >
                      {t('common.cancel', 'Cancel')}
                    </button>
                  )}
                  <button
                    onClick={savePin}
                    disabled={!pinValid || saving}
                    className={`flex-1 py-3 rounded-full font-semibold text-sm ${pinValid && !saving ? 'bg-orella-500 text-white' : 'bg-gray-200 text-gray-400'}`}
                  >
                    {saving ? t('security.saving', 'Saving…') : t('security.savePin', 'Save PIN')}
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex gap-2">
                <button
                  onClick={() => { setEditing(true); resetForm() }}
                  className="flex-1 py-3 bg-white border border-gray-200 rounded-full text-sm font-semibold text-gray-700"
                >
                  {t('security.changePin', 'Change PIN')}
                </button>
                <button
                  onClick={disablePin}
                  disabled={saving}
                  className="px-5 py-3 bg-white border border-gray-200 rounded-full text-sm font-semibold text-red-600 disabled:opacity-50"
                >
                  {t('security.turnOff', 'Turn off')}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
    </ResponsiveWrapper>
  )
}
